import { serviceClient } from "./commonLib";
import labels from "./labels";
import { v4 as uuidv4 } from "uuid";

const localLabel = (...args) => labels.getLocalLabel(...args);

/**
 * Query params have to be string, so we are emulating null values with a string 'null'.
 */
function nullableQueryParam(v) {
  if (v === null) return "null";
  return v;
}

export async function getCredentials({
  taskGuid,
  taskTypeGuid,
  userGuid,
  processGuid,
  resourceId,
  resourceKind,
  valueType,
} = {}) {
  const integrationsClient = await serviceClient.getClient("integrations");

  const result = await integrationsClient.apis.credentials.getCredentialsList({
    userGuid: nullableQueryParam(userGuid),
    taskTypeGuid: nullableQueryParam(taskTypeGuid),
    processGuid: nullableQueryParam(processGuid),
    taskGuid: nullableQueryParam(taskGuid),
    resourceId: nullableQueryParam(resourceId),
    resourceKind,
    valueType,
  });

  return result.body;
}

export async function getCredentialById(credentialId) {
  const integrationsClient = await serviceClient.getClient("integrations");

  const result = await integrationsClient.apis.credentials.getCredentialById({
    credentialId,
  });

  return result.body;
}

/**
 * @param {*} credentialId
 * @param {{ name, userGuid, taskTypeGuid, processGuid, taskGuid, resourceKind, resourceReferenceId, resourceId, valueType, value}} data
 */
export async function editCredential(credentialId, data) {
  const integrationsClient = await serviceClient.getClient("integrations");

  const result = await integrationsClient.apis.credentials.editCredential(
    { credentialId },
    { requestBody: data }
  );
  return result.body;
}

/**
 * @param {{ name, userGuid, taskTypeGuid, processGuid, taskGuid, resourceKind, resourceReferenceId, resourceId, valueType, value}} data
 */
export async function createCredential(data) {
  const integrationsClient = await serviceClient.getClient("integrations");

  const result = await integrationsClient.apis.credentials.createCredential(
    {},
    { requestBody: data }
  );
  return result.body;
}

export async function deleteCredential(credentialId) {
  const integrationsClient = await serviceClient.getClient("integrations");

  const result = await integrationsClient.apis.credentials.deleteCredential({
    credentialId,
  });
  return result.body;
}

export async function expandCredentialsPicker(credentialsPicker) {
  if (
    credentialsPicker.selectedCredentialsId &&
    !credentialsPicker.selectedCredentials
  ) {
    try {
      credentialsPicker.selectedCredentials = await getCredentialById(
        credentialsPicker.selectedCredentialsId
      );
    } catch (error) {
      console.error("Failed to get credential", error);
    }
  }
  if (
    credentialsPicker.selectedCredentials?.isCustom &&
    !credentialsPicker.sourceType
  ) {
    credentialsPicker.sourceType = "custom";
    credentialsPicker.valueType = credentialsPicker.selectedCredentials.valueType;
  }
}

export async function createIfNew(credentialsPicker) {
  const isNew =
    credentialsPicker.sourceType === "custom" && credentialsPicker.value;
  if (isNew) {
    const cred = await createCredential({
      resourceId: uuidv4(),
      isCustom: true,
      valueType: credentialsPicker.valueType,
      value: credentialsPicker.value,
    });
    credentialsPicker.selectedCredentials = cred;
    credentialsPicker.selectedCredentialsId = cred.id;
    credentialsPicker.value = undefined;
  }

  return credentialsPicker;
}

export function compactCredentialsPicker(credentialsPicker) {
  if (!credentialsPicker.type) return {}; // incomplete picker, resolving to default one

  if (credentialsPicker.type === "static") {
    if (!credentialsPicker.selectedCredentialsId) return {}; // incomplete picker, resolving to default one

    const canHaveField = getSubFields(
      credentialsPicker.selectedCredentials?.valueType
    );
    return {
      type: credentialsPicker.type,
      selectedCredentialsId: credentialsPicker.selectedCredentialsId,
      ...(canHaveField
        ? {
            field: credentialsPicker.field,
          }
        : {}),
    };
  } else if (credentialsPicker.type === "dynamic") {
    if (!credentialsPicker.resourceId) return {}; // incomplete picker, resolving to default one

    const canHaveField = getSubFields(credentialsPicker.valueType);
    return {
      type: credentialsPicker.type,
      valueType: credentialsPicker.valueType,
      resourceId: credentialsPicker.resourceId,
      ...(canHaveField
        ? {
            field: credentialsPicker.field,
          }
        : {}),
    };
  } else {
    console.error(
      new Error("Unknown credentials picker type"),
      credentialsPicker
    );
    return {};
  }
}

export const valueTypes = [
  {
    text: localLabel("Bearer token"),
    value: "bearerToken",
  },
  {
    text: localLabel("API Key"),
    value: "apiKey",
  },
  {
    text: localLabel("username/password"),
    value: "username-password",
  },
  {
    text: localLabel("Access/Secret key pair"),
    value: "aws",
  },
];
export const valueTypesDict = Object.fromEntries(
  valueTypes.map(({ text, value }) => [value, text])
);

export function getSubFields(valueType) {
  if (valueType === "username-password") {
    return [
      {
        text: localLabel("Username"),
        value: "username",
      },
      {
        text: localLabel("Password"),
        value: "password",
      },
    ];
  } else if (valueType === "aws") {
    return [
      {
        text: localLabel("Access Key"),
        value: "accessKey",
      },
      {
        text: localLabel("Secret Key"),
        value: "secretKey",
      },
    ];
  } else {
    return [];
  }
}

export const sourceTypes = [
  {
    text: localLabel("Credential Center"),
    value: "credentialCenter",
  },
  {
    text: localLabel("Custom"),
    value: "custom",
  },
];
export const sourceTypesDict = Object.fromEntries(
  valueTypes.map(({ text, value }) => [value, text])
);
